import {
  DiscordIcon,
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  PatreonIcon,
  PinterestIcon,
  SnapchatIcon,
  TelegramIcon,
  TiktokIcon,
  TumblrIcon,
  TwitterIcon,
  VimeoIcon,
  WebsiteIcon,
  WhatsappIcon,
  YoutubeIcon,
} from '../assets/icons/socials'
import { type SlugPattern, SocialItemEnum, type SocialNetworks } from '../types'

const makeValidateDomainPattern = (str: string) => ({
  match: regexp(`${str}/([^?#:]+).*`),
})

const makeValidateSlugPattern = (str: string, prepareSlug?: (str: string) => string) => ({
  match: new RegExp('^[a-z0-9-_@.]+$', 'i'),
  isSlugPattern: true,
  pattern: `https://${str}`,
  prepareSlug,
})

export const regexp = (str: string) => new RegExp(`(https?://)?(www.)?${str}`, 'i')

export const withHttps = (str: string) =>
  !str.includes('://') && !str.includes('mailto:') ? 'https://' + str : str

export const DefaultIcon = WebsiteIcon

export type SupportedSocialNetworks = Omit<SocialNetworks, SocialItemEnum.Zezam>

export const socialsDataMapper: SupportedSocialNetworks = {
  [SocialItemEnum.Website]: {
    icon: DefaultIcon,
    value: 'website',
    title: 'Website',
    validatePatterns: [{ match: new RegExp('(https?://)?(www.)?([^?#:]+).*', 'i') }],
  },
  [SocialItemEnum.Tumblr]: {
    icon: TumblrIcon,
    value: 'tumblr',
    title: 'Tumblr',
    validatePatterns: [
      makeValidateDomainPattern('tumblr.com'),
      makeValidateSlugPattern('tumblr.com/{SLUG}'),
    ],
  },
  [SocialItemEnum.Instagram]: {
    icon: InstagramIcon,
    value: 'instagram',
    title: 'Instagram',
    validatePatterns: [
      makeValidateDomainPattern('instagram.com'),
      makeValidateSlugPattern('instagram.com/{SLUG}', (slug) => slug.replace('@', '')),
    ],
  },
  [SocialItemEnum.Facebook]: {
    icon: FacebookIcon,
    value: 'facebook',
    title: 'Facebook',
    validatePatterns: [
      makeValidateDomainPattern('facebook.com'),
      makeValidateSlugPattern('facebook.com/{SLUG}'),
    ],
  },
  [SocialItemEnum.YouTube]: {
    icon: YoutubeIcon,
    value: 'youtube',
    title: 'YouTube',
    validatePatterns: [
      makeValidateDomainPattern('(youtube.com|youtu.be)'),
      makeValidateSlugPattern('www.youtube.com/@{SLUG}', (slug) => slug.replace(/^@/, '')),
    ],
  },
  [SocialItemEnum.TikTok]: {
    icon: TiktokIcon,
    value: 'tiktok',
    title: 'TikTok',
    validatePatterns: [
      makeValidateDomainPattern('tiktok.com'),
      makeValidateSlugPattern('www.tiktok.com/@{SLUG}', (slug) => slug.replace(/^@/, '')),
    ],
  },
  [SocialItemEnum.Pinterest]: {
    icon: PinterestIcon,
    value: 'pinterest',
    title: 'Pinterest',
    validatePatterns: [
      makeValidateDomainPattern('pinterest.([a-z]{2,3}|(co.uk))'),
      makeValidateSlugPattern('pinterest.com/{SLUG}'),
    ],
  },
  [SocialItemEnum.Snapchat]: {
    icon: SnapchatIcon,
    value: 'snapchat',
    title: 'Snapchat',
    validatePatterns: [
      makeValidateDomainPattern('snapchat.com'),
      makeValidateSlugPattern('snapchat.com/add/{SLUG}'),
    ],
  },
  [SocialItemEnum.Discord]: {
    icon: DiscordIcon,
    value: 'discord',
    title: 'Discord',
    validatePatterns: [
      makeValidateDomainPattern('discord.(com|gg)'),
      makeValidateSlugPattern('discord.com/{SLUG}'),
    ],
  },
  [SocialItemEnum.Linkedin]: {
    icon: LinkedinIcon,
    value: 'linkedin',
    title: 'Linkedin',
    validatePatterns: [
      makeValidateDomainPattern('linkedin.com'),
      makeValidateSlugPattern('linkedin.com/in/{SLUG}'),
    ],
  },
  [SocialItemEnum.Patreon]: {
    icon: PatreonIcon,
    value: 'patreon',
    title: 'Patreon',
    validatePatterns: [
      makeValidateDomainPattern('patreon.com'),
      makeValidateSlugPattern('patreon.com/{SLUG}'),
    ],
  },
  [SocialItemEnum.Telegram]: {
    icon: TelegramIcon,
    value: 'telegram',
    title: 'Telegram',
    validatePatterns: [makeValidateDomainPattern('t.me'), makeValidateSlugPattern('t.me/{SLUG}')],
  },
  [SocialItemEnum.Twitter]: {
    icon: TwitterIcon,
    value: 'twitter',
    title: 'Twitter',
    validatePatterns: [
      makeValidateDomainPattern('twitter.com'),
      makeValidateSlugPattern('twitter.com/{SLUG}'),
    ],
  },
  [SocialItemEnum.Vimeo]: {
    icon: VimeoIcon,
    value: 'vimeo',
    title: 'Vimeo',
    validatePatterns: [
      makeValidateDomainPattern('vimeo.com'),
      makeValidateSlugPattern('vimeo.com/{SLUG}'),
    ],
  },
  [SocialItemEnum.Whatsapp]: {
    icon: WhatsappIcon,
    value: 'whatsapp',
    title: 'Whatsapp',
    validatePatterns: [
      {
        match: new RegExp('^([+]?[(]?[0-9]{3}[)]?[-\\s.]?[0-9]{3}[-\\s.]?[0-9]{4,9}$)$'),
        isSlugPattern: true,
        pattern: 'https://api.whatsapp.com/send?phone={SLUG}',
      },
      {
        match: new RegExp(
          '(https?://)(www.)?api.whatsapp.com/send\\?phone=([+]?[(]?[0-9]{3}[)]?[-\\s.]?[0-9]{3}[-\\s.]?[0-9]{4,9}$)',
          'i',
        ),
      },
    ],
  },
}

const isSupportedSocialType = (type: SocialItemEnum): type is keyof SupportedSocialNetworks =>
  type in socialsDataMapper

export const getSocialNetwork = (type: SocialItemEnum) =>
  isSupportedSocialType(type) ? socialsDataMapper[type] : undefined

export const isSocialValid = (type: SocialItemEnum, str: string) => {
  const patterns = getSocialNetwork(type)?.validatePatterns
  str = str?.trim()

  return !!patterns && patterns.some((pattern) => pattern.match.test(str))
}

export const sanitizeSocialSlug = (type: SocialItemEnum, slug: string) => {
  if (!isSocialValid(type, slug)) {
    return withHttps(slug)
  }

  const sanitize = findSlugPattern(type)

  slug = (slug || '').trim()

  if (!sanitize?.match.test(slug)) {
    return withHttps(slug)
  }

  if (sanitize.prepareSlug) {
    slug = sanitize.prepareSlug(slug)
  }

  return sanitize.pattern.replace('{SLUG}', slug)
}

const findSlugPattern = (type: SocialItemEnum) =>
  getSocialNetwork(type)?.validatePatterns.find(
    (item): item is SlugPattern => 'isSlugPattern' in item,
  )

const isSocialItemType = (value: string): value is SocialItemEnum =>
  (Object.values(SocialItemEnum) as string[]).includes(value)

/**
 * Public profile URL of `handle` on `platform`, built from the network's slug pattern in
 * `socialsDataMapper`. `undefined` for an empty handle or a platform with no profile URL (`website`,
 * an unknown value).
 */
export const platformProfileUrl = (
  platform: string | undefined,
  handle: string | undefined,
): string | undefined => {
  const type = platform?.toLowerCase()
  const trimmedHandle = handle?.trim()
  if (!type || !trimmedHandle || !isSocialItemType(type)) {
    return undefined
  }

  const slugPattern = findSlugPattern(type)
  if (!slugPattern) {
    return undefined
  }

  const slug = slugPattern.prepareSlug ? slugPattern.prepareSlug(trimmedHandle) : trimmedHandle
  return slugPattern.pattern.replace('{SLUG}', encodeURIComponent(slug))
}
